import { useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Route as RouteIcon, Plus, Pencil, Trash2, Power, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import MasterTable from "@/components/MasterTable";

/** Delivery Routes Master (Phase 1 · Feb 2026)
 *  Fields: Name, Code, Company (implicit), Status, Runner (assigned delivery staff). Company Admin only. */
const EMPTY = { name: "", code: "", status: "active", runner_id: "" };

export default function RoutesPage() {
  const { user, tenant } = useAuth();
  const [items, setItems] = useState([]);
  const [runners, setRunners] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editForm, setEditForm] = useState({ id: "", ...EMPTY });

  const canManage = user?.role === "company_admin" || user?.role === "super_admin";

  const load = async () => {
    try {
      const { data } = await api.get("/routes");
      setItems(data || []);
    } catch (e) { toast.error("Failed to load routes"); }
  };
  const loadRunners = async () => {
    try {
      const { data } = await api.get("/delivery-staff");
      setRunners((data || []).filter((s) => s.active !== false));
    } catch { setRunners([]); }
  };
  useEffect(() => { load(); loadRunners(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/routes", { ...form, code: form.code.toUpperCase(), runner_id: form.runner_id || null });
      toast.success("Route added");
      setOpenCreate(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openEditFor = (r) => {
    setEditForm({ id: r.id, name: r.name, code: r.code, status: r.status || "active", runner_id: r.runner_id || "" });
    setOpenEdit(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const { id, ...body } = editForm;
      await api.patch(`/routes/${id}`, { ...body, code: body.code.toUpperCase(), runner_id: body.runner_id || null });
      toast.success("Route updated");
      setOpenEdit(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleStatus = async (r) => {
    const next = r.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/routes/${r.id}`, { status: next });
      toast.success(`Route ${next}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete route "${r.name}"? This does not delete existing parcels tagged with it.`)) return;
    try {
      await api.delete(`/routes/${r.id}`);
      toast.success("Route removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-8 space-y-6" data-testid="routes-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline flex items-center gap-2"><RouteIcon size={12}/> Delivery routes</div>
          <h1 className="text-4xl font-display mt-1">Routes.</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Group deliveries by geographic corridor and assign a default <b>Runner</b> per route.
          </p>
        </div>
        {canManage && (
          <Button data-testid="routes-new-btn" onClick={() => setOpenCreate(true)}
                  className="rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
            <Plus size={14} className="mr-2"/> Add route
          </Button>
        )}
      </header>

      <MasterTable
        columns={[
          { key: "name", label: "Name", render: (r) => <span className="font-semibold">{r.name}</span> },
          { key: "code", label: "Code", className: "mono text-xs" },
          { key: "company", label: "Company", className: "text-muted-foreground text-xs", sortable: false,
            render: () => tenant?.name || "—" },
          { key: "runner_name", label: "Runner", className: "text-xs",
            render: (r) => r.runner_name
              ? <span data-testid={`routes-runner-${r.id}`}>{r.runner_name}</span>
              : <span className="text-muted-foreground italic">Unassigned</span>
          },
          { key: "status", label: "Status", render: (r) => (
              <span data-testid={`routes-status-${r.id}`}
                    className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                      r.status === "active"
                        ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                        : "border-border bg-muted text-muted-foreground"}`}>
                {r.status}
              </span>
          )},
          { key: "actions", label: "", sortable: false, className: "text-right whitespace-nowrap",
            render: (r) => canManage ? (
              <div className="inline-flex items-center gap-3">
                <button data-testid={`routes-toggle-${r.id}`} onClick={() => toggleStatus(r)}
                        className="text-muted-foreground hover:text-primary"
                        title={r.status === "active" ? "Deactivate" : "Activate"}>
                  <Power size={14} />
                </button>
                <button data-testid={`routes-edit-${r.id}`} onClick={() => openEditFor(r)}
                        className="text-muted-foreground hover:text-primary">
                  <Pencil size={14} />
                </button>
                <button data-testid={`routes-delete-${r.id}`} onClick={() => remove(r)}
                        className="text-destructive hover:text-primary">
                  <Trash2 size={14} />
                </button>
              </div>
            ) : null,
          },
        ]}
        rows={items}
        searchKeys={["name", "code", "status", "runner_name"]}
        testidPrefix="routes"
        defaultSort={{ key: "name", dir: "asc" }}
        emptyText="No routes yet. Add one to start tagging parcels."
      />

      {/* Create dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="rounded-md max-w-md" data-testid="routes-create-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Add route</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <F label="Name" v={form.name} onChange={v => setForm({ ...form, name: v })} required t="routes-name" />
            <F label="Code" v={form.code} onChange={v => setForm({ ...form, code: v.toUpperCase() })} required t="routes-code" placeholder="e.g. DTAM" />
            <RunnerPicker runners={runners} value={form.runner_id}
              onChange={(v) => setForm({ ...form, runner_id: v })} testid="routes-runner" />
            <label className="block">
              <span className="overline">Status</span>
              <select data-testid="routes-status" value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
            <Button type="submit" data-testid="routes-submit"
                    className="w-full rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
              Save
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent className="rounded-md max-w-md" data-testid="routes-edit-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Edit route</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <F label="Name" v={editForm.name} onChange={v => setEditForm({ ...editForm, name: v })} required t="routes-edit-name" />
            <F label="Code" v={editForm.code} onChange={v => setEditForm({ ...editForm, code: v.toUpperCase() })} required t="routes-edit-code" />
            <RunnerPicker runners={runners} value={editForm.runner_id}
              onChange={(v) => setEditForm({ ...editForm, runner_id: v })} testid="routes-edit-runner" />
            <label className="block">
              <span className="overline">Status</span>
              <select data-testid="routes-edit-status" value={editForm.status}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                      className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
            <Button type="submit" data-testid="routes-edit-submit"
                    className="w-full rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
              Save changes
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RunnerPicker({ runners, value, onChange, testid }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return runners;
    return runners.filter((r) =>
      (r.name || "").toLowerCase().includes(needle) ||
      (r.phone || "").includes(needle) ||
      (r.code || "").toLowerCase().includes(needle));
  }, [runners, q]);
  const selected = runners.find((r) => r.id === value);
  return (
    <label className="block">
      <span className="overline">Runner (Delivery Staff)</span>
      <div className="mt-1 relative">
        <div className="relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <Input data-testid={`${testid}-search`}
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={selected ? `Selected: ${selected.name}` : "Search runners by name / phone…"}
            className="pl-8 rounded-md text-xs"/>
        </div>
        <select data-testid={testid} value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          size={Math.min(6, Math.max(3, filtered.length + 1))}
          className="mt-1 w-full px-3 rounded-md border border-input bg-background text-sm py-1">
          <option value="">— Unassigned —</option>
          {filtered.map((r) => (
            <option key={r.id} value={r.id} data-testid={`${testid}-opt-${r.id}`}>
              {r.name}{r.phone ? ` · ${r.phone}` : ""}{r.code ? ` (${r.code})` : ""}
            </option>
          ))}
        </select>
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">Pick from active Delivery Staff. Leave blank to keep the route unassigned.</div>
    </label>
  );
}

function F({ label, v, onChange, required, t, placeholder }) {
  return (
    <label className="block">
      <span className="overline">{label}{required && <span className="text-primary"> *</span>}</span>
      <Input data-testid={t} value={v || ""} onChange={e => onChange(e.target.value)}
             placeholder={placeholder} required={required}
             className="mt-1 rounded-md border-input focus-visible:ring-primary" />
    </label>
  );
}
